// ─────────────────────────────────────────────────────────────────────────────
// Client 360 — API hooks (TanStack Query)
// ─────────────────────────────────────────────────────────────────────────────

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Client360Response, ClientListFilters, ClientListResponse } from './types';

const BASE = '/api/clients';

export const clientKeys = {
  root: ['clients'] as const,
  list: (filters: ClientListFilters) => ['clients', 'list', filters] as const,
  detail: (id: string | null) => ['clients', 'detail', id] as const,
};

export function useClientList(filters: ClientListFilters) {
  return useQuery({
    queryKey: clientKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.hasOutstanding) params.set('hasOutstanding', 'true');
      if (filters.sortBy) params.set('sortBy', filters.sortBy);
      if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.skip) params.set('skip', String(filters.skip));
      return (await api.get<ClientListResponse>(`${BASE}?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useClient360(id: string | null) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: async () => {
      if (!id) throw new Error('No client ID');
      // Client 360 is a complete client history, not a paged list preview.
      // The endpoint caps this safely at 100 records per section.
      return (await api.get<Client360Response>(`${BASE}/${id}/360?limit=100`)).data;
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}
